import { MoreVertical, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface NotificationTrayHeaderProps {
  onClose: () => void;
  onMarkAllAsRead: () => void;
  onDeleteAll: () => void;
  isMarkingAllAsRead?: boolean;
  isDeletingAll?: boolean;
  /** Whether to show the "Mark all as read" option */
  hasUnreadNotifications?: boolean;
  /** Whether there are any notifications at all */
  hasNotifications?: boolean;
}

/**
 * Header component for the notification tray.
 * Contains title, close button, and dropdown menu with "Mark all as read" and "Clear all" options.
 */
export function NotificationTrayHeader({
  onClose,
  onMarkAllAsRead,
  onDeleteAll,
  isMarkingAllAsRead,
  isDeletingAll,
  hasUnreadNotifications = false,
  hasNotifications = false,
}: NotificationTrayHeaderProps) {
  const { t } = useTranslation('authenticated.notifications');

  const showMenu = hasUnreadNotifications || hasNotifications;

  return (
    <div className="flex items-center justify-between px-4 py-4">
      <h2 className="text-lg font-semibold">{t('inbox.title')}</h2>
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={onClose}
          aria-label={t('inbox.close')}
        >
          <X className="h-4 w-4" />
        </Button>
        {showMenu && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {hasUnreadNotifications && (
                <DropdownMenuItem onClick={onMarkAllAsRead} disabled={isMarkingAllAsRead}>
                  {t('inbox.markAllAsRead')}
                </DropdownMenuItem>
              )}
              {hasNotifications && (
                <DropdownMenuItem onClick={onDeleteAll} disabled={isDeletingAll}>
                  {t('inbox.clearAll')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}
